import { Anchor, Flex, Group, Text } from '@mantine/core';
import { IconAlertTriangle, IconArrowLeft, IconArrowRight, IconUsers } from '@tabler/icons-react';
import { Client } from 'src/admin/users/types';
import { useCurrentFamilyContext } from 'src/clients/context/useCurrentFamilyContext';
import { useClient } from 'src/clients/hooks/useClient';
import { useClients } from 'src/clients/hooks/useClients';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { formatName, NameFormat } from 'src/utils/nameFormatting';
import { ChatMode } from '../chat/types';
import { ContentMeta } from '../types';

export type PreviousChatContext = Pick<ContentMeta, 'entityId' | 'chatMode'> & {
  clientId: string | undefined;
  url: string;
};

export function DisplacedChatBanner({
  previousChatContext,
  onGoBack,
  onMoveToFamily,
}: {
  previousChatContext: PreviousChatContext | null;
  onGoBack: () => void;
  onMoveToFamily: () => void;
}) {
  const { flagEnabledPaginatedClients, flagEnabledFamilyScreenRework } = useFlags();
  const { householdFamilyId } = useCurrentFamilyContext();

  const single = useClient(
    flagEnabledPaginatedClients && previousChatContext?.chatMode === ChatMode.CLIENT
      ? previousChatContext.entityId
      : undefined,
    // A stale or non-client entityId resolves to a 404 we treat as an expected
    // miss, so disable the global default retries.
    { retry: false },
  );
  const list = useClients({}, { enabled: !flagEnabledPaginatedClients });

  if (!previousChatContext?.url) return null;

  const resolvedClient = flagEnabledPaginatedClients
    ? single.client
    : (list.clients.find(c => c.id === previousChatContext.entityId) ?? null);

  const canMoveToFamily =
    flagEnabledFamilyScreenRework &&
    householdFamilyId != null &&
    resolvedClient?.familyId === householdFamilyId;

  if (canMoveToFamily) {
    return (
      <Flex direction="column" gap="xxs" p="sm">
        <Group gap="xxs" align="center">
          <IconUsers size={14} color="var(--mantine-color-dimmed)" stroke={1.5} aria-hidden />
          <Text size="sm" c="dimmed">
            This chat can continue across the whole household
          </Text>
        </Group>
        <Anchor component="button" type="button" size="sm" onClick={onMoveToFamily}>
          <Group gap="xxs" align="center">
            <IconArrowRight size={14} stroke={1.5} aria-hidden />
            <Text size="sm" fw={FONT_WEIGHTS.medium}>
              Move this conversation into the family context
            </Text>
          </Group>
        </Anchor>
      </Flex>
    );
  }

  const linkLabel = buildResumeChatLinkLabel(previousChatContext, resolvedClient);

  return (
    <Flex direction="column" gap="xxs" p="sm">
      <Group gap="xxs" align="center">
        <IconAlertTriangle size={14} color="var(--mantine-color-dimmed)" stroke={1.5} aria-hidden />
        <Text size="sm" c="dimmed">
          Your next message will begin a new chat
        </Text>
      </Group>
      <Anchor component="button" type="button" size="sm" onClick={onGoBack}>
        <Group gap="xxs" align="center">
          <IconArrowLeft size={14} stroke={1.5} aria-hidden />
          <Text size="sm" fw={FONT_WEIGHTS.medium}>
            {linkLabel}
          </Text>
        </Group>
      </Anchor>
    </Flex>
  );
}

function buildResumeChatLinkLabel(
  previousChatContext: PreviousChatContext,
  resolvedClient: Client | null,
): string {
  if (previousChatContext.chatMode === ChatMode.DOCUMENT) {
    return 'Return to document to resume chat';
  }
  if (previousChatContext.chatMode === ChatMode.CLIENT && resolvedClient) {
    const name = formatName(resolvedClient.firstName, resolvedClient.lastName, NameFormat.NATURAL);
    return `Return to ${name} to resume chat`;
  }
  return 'Return to client to resume chat';
}
